import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface Broker {
  name: string
  email: string
  /** Already formatted for display, e.g. "(945) 202-3186". */
  phone: string
  /** Dialable form of `phone` for a `tel:` link; '' when it isn't a real number. */
  phoneE164: string
  /** Public-bucket headshot URL, or '' when the broker has not uploaded one. */
  photoUrl: string
}

/**
 * The Cai broker assigned to `orgId`, or to the signed-in user's own org when it is
 * omitted — null when there isn't one to show. The backend decides: only live orgs
 * owned by a real, active person get a broker back, never the email-only fallback,
 * and only Cai staff may name an org other than their own.
 */
export function useOrgBroker(orgId?: string) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_BROKER, orgId],
    queryFn: () =>
      axios
        .get('/api/v1/insure/users/me/broker/', { params: orgId ? { org: orgId } : undefined })
        .then((response) => (toCamelCase(response.data) as { broker: Broker | null }).broker),
    // Assignment changes at most a handful of times in an org's lifetime, so
    // don't re-fetch on every remount of the widget as the user navigates.
    staleTime: 5 * 60 * 1000,
  })

  return { broker: data ?? null, isLoading }
}

/** Mirrors BROKER_NOTE_MAX_LENGTH in insure/services/insurance/broker_note_service.py. */
export const BROKER_NOTE_MAX_LENGTH = 2000

function sendBrokerNote(content: string): Promise<void> {
  return axios.post('/api/v1/insure/users/me/broker/note/', { content }).then(() => undefined)
}

/** Emails the signed-in user's matched broker a note. Replies go straight to the user. */
export function useSendBrokerNote() {
  return useMutation({ mutationFn: sendBrokerNote })
}
